import { useCallback, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { Bell, Inbox } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader } from "@/components/common";
import { useTran } from "@/hooks";
import { showToast } from "@/lib/utils/toast";
import { useNotificationsStore } from "../hooks";
import { formatRelativeTime } from "../utils/formatRelativeTime";

const NotificationsPage = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const location = useLocation();
  const store = useNotificationsStore();
  const observerRef = useRef(null);
  const markedAsReadRef = useRef(new Set());

  const focusNotificationId = location?.state?.focusNotificationId || null;

  const formatTypeLabel = useCallback(
    (type) => {
      if (!type) return t("notifications.type");
      return type
        .toString()
        .replace(/[_.]/g, " ")
        .replace(/\b\w/g, (chr) => chr.toUpperCase());
    },
    [t],
  );

  useEffect(() => {
    store.loadNotifications();
  }, [store]);

  const rows = useMemo(
    () =>
      store.items.map((item) => {
        const actorName =
          item?.actor?.name || item?.actor?.email || t("notifications.someone");
        const target = store.resolveNotificationTarget(item);
        return {
          id: item?.id || item?.message,
          message: item?.message || "",
          time: formatRelativeTime(item?.createdAt, {
            fallback: t("notifications.justNow"),
          }),
          typeLabel: formatTypeLabel(item?.type),
          actor: actorName,
          unread: !item?.readAt,
          actionLabel: target?.label || item?.action?.label || null,
          url: target?.url || null,
          markRead: target?.markRead !== false,
          targetState: target?.state || null,
          raw: item,
        };
      }),
    [formatTypeLabel, store.items, store, t],
  );

  const headerDescription =
    store.unread > 0
      ? t("notifications.unreadSummary", { count: store.unread })
      : t("notifications.emptySummary");

  const handleOpenAction = async (row) => {
    if (!row) return;
    if (row.markRead && row.id) {
      const success = await store.markReadById(row.id);
      if (success === false) {
        showToast.error(t("notifications.markReadError"));
      }
    }

    if (!row.url) return;
    const isExternal = /^https?:\/\//i.test(row.url);
    if (isExternal && typeof window !== "undefined") {
      window.location.href = row.url;
      return;
    }

    navigate(row.url, { state: row.targetState });
  };

  const handleRemove = (row) => {
    if (!row?.id) return;
    store.removeById(row.id);
  };

  const handleClearAll = async () => {
    const snapshot = rows.length;
    const markMeta = await store.markAllRead();
    const clearMeta = await store.clearAll();
    const count = clearMeta?.cleared ?? markMeta?.updated ?? snapshot;
    if (clearMeta) {
      showToast.success(t("notifications.clearAllSuccess", { count }));
    } else {
      showToast.error(t("notifications.clearAllError"));
    }
  };

  const handleMarkAllRead = async () => {
    const meta = await store.markAllRead();
    const count = meta?.updated ?? rows.length;
    if (meta) {
      showToast.success(t("notifications.markAllReadSuccess", { count }));
    } else {
      showToast.error(t("notifications.markAllReadError"));
    }
  };

  // Setup Intersection Observer to mark notifications without buttons as read when they come into view
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const notificationId = entry.target.getAttribute(
            "data-notification-id",
          );
          // Only mark as read if this notification has NO button (data-has-button="false")
          const hasButton = entry.target.getAttribute("data-has-button");

          if (
            notificationId &&
            hasButton === "false" &&
            !markedAsReadRef.current.has(notificationId)
          ) {
            markedAsReadRef.current.add(notificationId);
            // Mark as read when notification without button enters viewport
            store.markReadById(notificationId);
          }
        }
      });
    };

    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
    });

    const notificationCards = document.querySelectorAll(
      "[data-notification-id]",
    );
    notificationCards.forEach((card) => {
      observerRef.current?.observe(card);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [rows, store]);

  // Scroll to focused notification
  useEffect(() => {
    if (!focusNotificationId || typeof window === "undefined") return;
    const el = document.querySelector(
      `[data-notification-id="${focusNotificationId}"]`,
    );
    if (el && typeof el.scrollIntoView === "function") {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [focusNotificationId, rows]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("notifications.title")}
        description={headerDescription}
        icon={Bell}
        actions={
          rows.length > 0 ? (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <Button size="sm" onClick={handleMarkAllRead}>
                {t("notifications.markAllRead")}
              </Button>
              <Button size="sm" variant="outline" onClick={handleClearAll}>
                {t("notifications.clearAll")}
              </Button>
            </div>
          ) : null
        }
      />

      <main className="space-y-4">
        {rows.length === 0 ? (
          <Card className="border border-dashed">
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center text-muted-foreground">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Inbox className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <p className="text-base font-semibold text-foreground">
                  {t("notifications.empty")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("notifications.emptyHint")}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {rows.map((row) => (
              <Card
                key={row.id}
                className={`border transition-shadow ${
                  row.unread
                    ? "border-primary/50 bg-card shadow-sm"
                    : "bg-card/70"
                } ${
                  focusNotificationId === row.id
                    ? "ring-2 ring-primary/50 ring-offset-2 ring-offset-background"
                    : ""
                }`}
                data-notification-id={row.id}
                data-has-button={row.url ? "true" : "false"}
              >
                <CardContent className="flex flex-col gap-3 p-4 sm:p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span
                        className={`mt-0.5 inline-flex h-2.5 w-2.5 rounded-full ${
                          row.unread ? "bg-primary" : "bg-muted"
                        }`}
                      />
                      <div className="space-y-0.5">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">
                          {row.typeLabel}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {row.time}
                        </p>
                      </div>
                    </div>
                    {row.unread ? (
                      <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                        {t("notifications.unread")}
                      </span>
                    ) : null}
                  </div>

                  <div className="space-y-1">
                    <p
                      className={`text-sm font-semibold ${
                        row.unread ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {row.message}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>
                        {t("notifications.from")}:
                        <b className="ml-1 font-semibold text-foreground">
                          {row.actor}
                        </b>
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {row.url && row.actionLabel ? (
                      <Button
                        size="sm"
                        onClick={() => handleOpenAction(row)}
                        className="w-full sm:w-auto"
                      >
                        {row.actionLabel}
                      </Button>
                    ) : row.url ? (
                      <Button
                        size="sm"
                        onClick={() => handleOpenAction(row)}
                        className="w-full sm:w-auto"
                      >
                        {t("notifications.view")}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="destructive"
                      className="w-full sm:w-auto"
                      onClick={() => handleRemove(row)}
                    >
                      {t("notifications.dismiss")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
});

export default NotificationsPage;
